<script setup>
import Button from "primevue/button";
import { computed, onUnmounted } from "vue";
import { usePage } from "@inertiajs/vue3";
import { trans } from 'laravel-vue-i18n';

const PROVIDER_META = {
    google: { label: trans('forms.cal_google_label'), icon: 'pi pi-google' },
    microsoft: { label: 'Microsoft 365', icon: 'pi pi-microsoft' },
}

const props = defineProps({
    calendars: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    error: { type: Boolean, default: false },
    reconnectProviders: { type: Array, default: () => [] },
})

const emit = defineEmits(['refresh', 'select'])
const model = defineModel()

const columns = computed(() => props.calendars.length > 6 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1')

const userTimezone = usePage().props.user?.timezone

const select = (item) => {
    model.value = item.id
    emit('select', item)
}

const openConnectPopup = (provider) => {
    window.open(
        `/connect/${provider}/popup_calendar`,
        'roori_calendar_connect',
        'width=500,height=680,popup=yes,left=200,top=100'
    )
}

const onMessage = (event) => {
    if (event.data === 'calendar_connected') {
        emit('refresh')
    }
}

window.addEventListener('message', onMessage)
onUnmounted(() => window.removeEventListener('message', onMessage))

</script>

<template>
    <div class="rounded-2xl border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900 overflow-hidden">

        <!-- Loading -->
        <div v-if="loading" class="grid grid-cols-1 md:grid-cols-2 gap-2 p-4">
            <div v-for="i in 6" :key="i" class="h-14 rounded-lg bg-neutral-200 dark:bg-neutral-700 animate-pulse"></div>
        </div>

        <!-- Error -->
        <div v-else-if="error" class="flex flex-col items-center gap-4 py-10 text-center px-6">
            <span class="pi pi-exclamation-circle text-amber-500 text-2xl"></span>
            <p class="text-sm text-gray-600 dark:text-gray-300">
                {{ $t('forms.cal_error_load') }}<br>
                {{ $t('forms.cal_error_token') }}
            </p>
            <div class="flex flex-wrap gap-2 justify-center">
                <Button
                    v-for="provider in reconnectProviders"
                    :key="provider"
                    :label="$t('forms.cal_login_via', { provider: PROVIDER_META[provider]?.label ?? provider })"
                    :icon="PROVIDER_META[provider]?.icon"
                    size="small"
                    severity="secondary"
                    @click="openConnectPopup(provider)"
                />
                <Button :label="$t('forms.cal_retry')" size="small" severity="secondary" text icon="pi pi-refresh" @click="emit('refresh')" />
            </div>
        </div>

        <!-- Prázdný stav -->
        <div v-else-if="!calendars.length" class="flex flex-col items-center gap-4 py-10 text-center px-6">
            <span class="pi pi-calendar text-neutral-300 dark:text-neutral-600 text-3xl"></span>
            <template v-if="reconnectProviders.length">
                <p class="text-sm text-gray-500 dark:text-gray-400">{{ $t('forms.cal_connect_account') }}</p>
                <div class="flex flex-wrap gap-2 justify-center">
                    <Button
                        v-for="provider in reconnectProviders"
                        :key="provider"
                        :label="$t('forms.cal_login_via', { provider: PROVIDER_META[provider]?.label ?? provider })"
                        :icon="PROVIDER_META[provider]?.icon"
                        size="small"
                        severity="secondary"
                        @click="openConnectPopup(provider)"
                    />
                </div>
            </template>
            <p v-else class="text-sm text-gray-500 dark:text-gray-400">{{ $t('forms.cal_none_available') }}</p>
        </div>

        <!-- Seznam -->
        <div v-else class="grid gap-2 max-h-96 overflow-y-auto p-3" :class="columns">
            <button
                v-for="item in calendars"
                :key="item.id"
                @click="select(item)"
                class="flex items-start gap-3 px-4 py-2.5 rounded-lg text-left transition-colors bg-white dark:bg-neutral-800/60 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                :class="model === item.id ? 'bg-neutral-100 dark:bg-neutral-800' : ''"
            >
                <span
                    class="w-3 h-3 rounded-full shrink-0 ring-2 ring-white dark:ring-neutral-800 mt-1"
                    :style="{ backgroundColor: item.color || '#9ca3af' }"
                ></span>

                <div class="grow min-w-0">
                    <div class="flex items-center gap-2 flex-wrap">
                        <span class="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">{{ item.name }}</span>
                        <span v-if="item.primary"
                              class="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 shrink-0">
                            {{ $t('forms.cal_primary') }}
                        </span>
                        <span v-if="item.timezone && userTimezone && item.timezone !== userTimezone"
                              class="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-300 shrink-0"
                              :title="$t('forms.cal_timezone_tooltip', { user_tz: userTimezone, cal_tz: item.timezone })">
                            {{ $t('forms.cal_timezone_mismatch') }}
                        </span>
                    </div>
                    <p v-if="item.description" class="text-xs text-gray-400 dark:text-gray-500 truncate mt-0.5">{{ item.description }}</p>
                    <div class="flex items-center gap-2 mt-0.5 flex-wrap">
                        <p v-if="item.email" class="text-xs text-gray-400 dark:text-gray-500 truncate">{{ item.email }}</p>
                        <span v-if="item.email && item.timezone" class="text-gray-300 dark:text-gray-600 text-xs">·</span>
                        <p v-if="item.timezone" class="text-xs text-gray-400 dark:text-gray-500 shrink-0">{{ item.timezone }}</p>
                    </div>
                </div>

                <span v-if="model === item.id" class="pi pi-check text-xs text-neutral-400 shrink-0 mt-1"></span>
            </button>
        </div>
    </div>
</template>
